import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ToolCard } from '../components/business/ToolCard'
import { AdSlot } from '../components/ads/AdSlot'
import { ShelfHeader } from '../components/ShelfHeader'
import categories from '../data/categories.json'
import items from '../data/item.json'
import type { Tool } from '../types'
import type { LucideIcon } from 'lucide-react'
import {
  TrendingUp,
  Newspaper,
  PieChart,
  Wrench,
  Building2,
  Cpu,
  BookOpen,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  quanshang: Building2,
  jiagexingwei: TrendingUp,
  zixun: Newspaper,
  qiquan: PieChart,
  gongju: Wrench,
  web3: Cpu,
  yingyu_xuexi: BookOpen,
}

/** App Store shelf: 每列 N 行，横向滑动 */
function chunkTools(tools: Tool[], size: number): Tool[][] {
  const chunks: Tool[][] = []
  for (let i = 0; i < tools.length; i += size) {
    chunks.push(tools.slice(i, i + size))
  }
  return chunks
}

function ShelfRail({ children, className = '' }: { children: ReactNode; className?: string }) {
  const railRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateScrollState = () => {
    const rail = railRef.current
    if (!rail) return

    setCanScrollLeft(rail.scrollLeft > 4)
    setCanScrollRight(rail.scrollLeft + rail.clientWidth < rail.scrollWidth - 4)
  }

  useEffect(() => {
    const rail = railRef.current
    if (!rail) return

    updateScrollState()
    const observer = new ResizeObserver(updateScrollState)
    observer.observe(rail)
    return () => observer.disconnect()
  }, [])

  const scrollByPage = (direction: 'left' | 'right') => {
    const rail = railRef.current
    if (!rail) return

    rail.scrollBy({
      left: direction === 'left' ? -rail.clientWidth * 0.85 : rail.clientWidth * 0.85,
      behavior: 'smooth',
    })
  }

  return (
    <div className="group/rail relative">
      <div
        ref={railRef}
        onScroll={updateScrollState}
        className={`flex overflow-x-auto no-scrollbar snap-x snap-mandatory -mx-5 px-5 md:-mx-6 md:px-6 ${className}`}
      >
        {children}
      </div>
      {canScrollLeft ? (
        <button
          type="button"
          aria-label="向左查看"
          onClick={() => scrollByPage('left')}
          className="absolute left-0 top-1/2 z-10 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-background/80 text-foreground opacity-0 shadow-[0_2px_10px_rgb(0_0_0_/_0.12)] backdrop-blur-sm transition duration-200 hover:bg-background hover:shadow-[0_3px_14px_rgb(0_0_0_/_0.16)] focus-visible:opacity-100 md:group-hover/rail:flex md:group-hover/rail:opacity-100"
        >
          <ChevronLeft className="w-5 h-5 stroke-[2.5]" />
        </button>
      ) : null}
      {canScrollRight ? (
        <button
          type="button"
          aria-label="向右查看"
          onClick={() => scrollByPage('right')}
          className="absolute right-0 top-1/2 z-10 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-background/80 text-foreground opacity-0 shadow-[0_2px_10px_rgb(0_0_0_/_0.12)] backdrop-blur-sm transition duration-200 hover:bg-background hover:shadow-[0_3px_14px_rgb(0_0_0_/_0.16)] focus-visible:opacity-100 md:group-hover/rail:flex md:group-hover/rail:opacity-100"
        >
          <ChevronRight className="w-5 h-5 stroke-[2.5]" />
        </button>
      ) : null}
    </div>
  )
}

function ShelfColumn({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={`snap-start min-w-[min(100%,300px)] flex-[1_0_25%] divide-y divide-border/40 border-t border-border/40 pr-5 sm:pr-8 last:pr-0 ${className}`}
    >
      {children}
    </div>
  )
}

export default function HomePage() {
  const { categoryCounts, categoryGroups, chartTools, featuredColumns } =
    useMemo(() => {
      const toolsByCategory: Record<string, Tool[]> = {}
      const featuredTools: Tool[] = []

      for (const tool of items as Tool[]) {
        const categoryItems = toolsByCategory[tool.categoryId] ?? []
        categoryItems.push(tool)
        toolsByCategory[tool.categoryId] = categoryItems

        if (tool.featured) {
          featuredTools.push(tool)
        }
      }

      const counts = Object.fromEntries(
        Object.entries(toolsByCategory).map(([categoryId, tools]) => [
          categoryId,
          tools.length,
        ]),
      )

      // 分类货架：每类最多 9 条 → 3 列 × 3 行（App Store SmallLockup rows=3）
      const groups = categories
        .filter((cat) => cat.featured)
        .map((cat) => ({
          category: cat,
          columns: chunkTools((toolsByCategory[cat.id] ?? []).slice(0, 9), 3),
        }))
        .filter((group) => group.columns.length > 0)

      const charts = [...(items as Tool[])]
        .sort((a, b) => b.rating - a.rating || b.reviews - a.reviews)
        .slice(0, 12)

      const featured = featuredTools.slice(0, 12)

      return {
        categoryCounts: counts,
        categoryGroups: groups,
        chartTools: charts,
        featuredColumns: chunkTools(featured, 3),
      }
    }, [])

  return (
    <div className="min-h-full bg-background">
      <div className="max-w-[1440px] mx-auto px-5 md:px-8 lg:px-10 pt-7 md:pt-9 pb-16 md:pb-24">
        {/* Page header — matches App Store "Apps" title scale */}
        <header className="mb-7 md:mb-9 pb-5 border-b border-border/50">
          <h1 className="text-[32px] md:text-[40px] font-bold tracking-tight leading-none">
            发现
          </h1>
        </header>

        <div className="space-y-10 md:space-y-12">
          {/* 推广 shelf */}
          <AdSlot slotId="home_hero" />

          {/* 编辑精选 — SmallLockup 3 rows */}
          {featuredColumns.length > 0 && (
            <section>
              <ShelfHeader title="编辑精选" />
              <ShelfRail>
                {featuredColumns.map((column, colIdx) => (
                  <ShelfColumn key={colIdx}>
                    {column.map((tool) => (
                      <ToolCard key={tool.id} tool={tool} />
                    ))}
                  </ShelfColumn>
                ))}
              </ShelfRail>
            </section>
          )}

          {chartTools.length > 0 && (
            <section>
              <ShelfHeader title="热门排行" />
              <ShelfRail className="gap-5 md:gap-6 pb-1">
                {chartTools.map((tool, index) => (
                  <ToolCard
                    key={tool.id}
                    tool={tool}
                    rank={index + 1}
                    chartStyle
                  />
                ))}
              </ShelfRail>
            </section>
          )}

          <AdSlot slotId="home_mid" />

          {/* 分类货架 — 每类 3×N SmallLockup + 查看全部 */}
          {categoryGroups.map(({ category, columns }) => (
            <section key={category.id}>
              <ShelfHeader
                title={category.name}
                seeAllHref={`/category/${category.id}`}
              />
              <ShelfRail>
                {columns.map((column, colIdx) => (
                  <ShelfColumn key={colIdx}>
                    {column.map((tool) => (
                      <ToolCard key={tool.id} tool={tool} />
                    ))}
                  </ShelfColumn>
                ))}
              </ShelfRail>
            </section>
          ))}

          {/* 浏览分类 — 横向大块，接近 App Store 分类入口 */}
          <section>
            <ShelfHeader title="浏览分类" />
            <ShelfRail className="gap-3 pb-1">
              {categories.map((cat) => {
                const count = categoryCounts[cat.id] ?? 0
                const Icon = CATEGORY_ICONS[cat.id] || PieChart
                return (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.id}`}
                    className="snap-start shrink-0 w-[148px] sm:w-[160px] rounded-2xl bg-secondary/80 hover:bg-secondary transition-colors p-4 flex flex-col justify-between min-h-[120px]"
                  >
                    <div className="w-9 h-9 rounded-[22%] bg-background/80 flex items-center justify-center border border-border/30">
                      <Icon className="w-[18px] h-[18px] text-primary" />
                    </div>
                    <div className="mt-4">
                      <div className="flex items-center gap-0.5">
                        <h3 className="font-semibold text-[15px] leading-tight truncate">
                          {cat.name}
                        </h3>
                        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                      </div>
                      <p className="text-[12px] text-muted-foreground mt-0.5">
                        {count} 个
                      </p>
                    </div>
                  </Link>
                )
              })}
            </ShelfRail>
          </section>
        </div>
      </div>
    </div>
  )
}
